import React, { useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import styles from './RandomCube.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getUserId } from "../../utils/userId";
import { v4 as uuidv4 } from "uuid";
import { doc, serverTimestamp, setDoc, Timestamp } from "@firebase/firestore";
import { db } from "../../utils/firebase";
import {
  generateTestId,
  getCognitiveTestId,
  setCognitiveTestId,
  getCognitiveTestSetId,
} from "../../utils/testId";

// 선택될 문자 목록
const CHARACTERS: string[] = ['1', '3', '8', '0', '+', '#', 'ㅁ'];
// 큐브 크기 (픽셀 단위)
const SMALL_CUBE_SIZE: number = 100;
// 게임 설정
const QUESTION_TIME: number = 15;
const TOTAL_QUESTIONS: number = 5;

// 배열 섞기 헬퍼 함수
function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

// 모든 가능한 회전 조합
const ROTATIONS = [
  'rotateX(-20deg) rotateY(45deg) rotateZ(0deg)',
  'scaleX(-1) rotateX(-20deg) rotateY(45deg) rotateZ(0deg)',
  'scaleY(-1) rotateX(-25deg) rotateY(55deg) rotateZ(-15deg)',
  'scaleY(-1) rotateX(-25deg) rotateY(35deg) rotateZ(10deg)',
  'rotateX(-10deg) rotateY(50deg) rotateZ(-15deg)',
  'rotateX(-25deg) rotateY(45deg) rotateZ(10deg)',
  'scaleX(-1) rotateX(-10deg) rotateY(45deg) rotateZ(-10deg)',
  'scaleX(-1) scaleY(-1) rotateX(-40deg) rotateY(40deg) rotateZ(20deg)',
  'scaleX(-1) scaleY(-1) rotateX(-20deg) rotateY(45deg)'
];

type GameState = 'start' | 'playing' | 'result';

const RandomCube: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';  
  
  const handleBack = () => {
    navigate(`${prefix}/cognitive/check/new`);
  };
  
  const [gameState, setGameState] = useState<GameState>('start');
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
  const [score, setScore] = useState(0);
  const [selectedCube, setSelectedCube] = useState<number | null>(null);
  const [mainRotation, setMainRotation] = useState('');
  const [smallRotations, setSmallRotations] = useState<string[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedChars, setSelectedChars] = useState<string[]>([]);
  
  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden'; // 잠시 스크롤 막음 (깜빡임 방지)
    document.body.style.overflow = 'hidden';
    
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // 0.1초 뒤에 스크롤 다시 품 (자연스럽게)
    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 100);

    return () => clearTimeout(timer);
  }, []);
  
  // 초기 문자 선택
  useEffect(() => {
    const shuffled = shuffleArray(CHARACTERS);
    setSelectedChars(shuffled.slice(0, 6));
  }, []);

  // 초기 회전 설정
  useEffect(() => {
    const mainRotationIndex = Math.floor(Math.random() * ROTATIONS.length);
    const shuffledRotations = shuffleArray(ROTATIONS);
    setMainRotation(ROTATIONS[mainRotationIndex]);
    setSmallRotations(shuffledRotations);
  }, []);

  // 타이머 효과
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      // 시간 초과시 다음 문제로
      if (currentQuestion < TOTAL_QUESTIONS) {
        nextQuestion();
      } else {
        setGameState('result');
      }
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft, currentQuestion]);

  // 다음 문제로 이동
  const nextQuestion = () => {
    setCurrentQuestion(prev => prev + 1);
    setTimeLeft(QUESTION_TIME);
    setSelectedCube(null);
    // 새로운 문자와 회전 조합 생성
    const shuffled = shuffleArray(CHARACTERS);
    setSelectedChars(shuffled.slice(0, 6));
    const mainRotationIndex = Math.floor(Math.random() * ROTATIONS.length);
    const shuffledRotations = shuffleArray(ROTATIONS);
    setMainRotation(ROTATIONS[mainRotationIndex]);
    setSmallRotations(shuffledRotations);
  };

  // 게임 시작
  const startGame = () => {
    setGameState('playing');
    setTimeLeft(QUESTION_TIME);
    setScore(0);
    setCurrentQuestion(1);
    setSelectedCube(null);
    // 새로운 문자와 회전 조합 생성
    const shuffled = shuffleArray(CHARACTERS);
    setSelectedChars(shuffled.slice(0, 6));
    const mainRotationIndex = Math.floor(Math.random() * ROTATIONS.length);
    const shuffledRotations = shuffleArray(ROTATIONS);
    setMainRotation(ROTATIONS[mainRotationIndex]);
    setSmallRotations(shuffledRotations);
  };

  // 큐브 선택 처리
  const handleCubeClick = (index: number) => {
    if (gameState !== 'playing') return;
    
    setSelectedCube(index);
    // 정답인 경우에만 점수 증가
    if (smallRotations[index] === mainRotation) {
      setScore(prev => prev + 1);
    }
    // 정답 여부와 관계없이 다음 문제로 이동
    if (currentQuestion < TOTAL_QUESTIONS) {
      nextQuestion();
    } else {
      setGameState('result');
    }
  };

  // CSS 변수를 사용하기 위한 스타일 객체
  const cubeContainerStyle: React.CSSProperties = {
    '--cube-size': `${SMALL_CUBE_SIZE}px`,
  } as React.CSSProperties;

  const playingOptionCubeStyle: React.CSSProperties = {
    '--cube-size': `60px`, // 60px로 설정
} as React.CSSProperties;

  const renderCube = (chars: string[], containerStyle: React.CSSProperties, cubeStyle?: React.CSSProperties, className?: string, onClick?: () => void) => (
  <div className={`${styles.cubeContainer} ${className || ''}`} style={containerStyle} onClick={onClick}>
      <div className={styles.cube} style={cubeStyle}>
        <div className={styles.cubeFaceFront}>{chars[0]}</div>
        <div className={styles.cubeFaceRight}>{chars[1]}</div>
        <div className={styles.cubeFaceTop}>{chars[2]}</div>
        <div className={styles.cubeFaceBack}>{chars[3]}</div>
        <div className={styles.cubeFaceLeft}>{chars[4]}</div>
        <div className={styles.cubeFaceBottom}>{chars[5]}</div>
      </div>
    </div>
  );

  const saveResultsToFirebase = async () => {
    try {
      const userId = getUserId();
      const testSetId = getCognitiveTestSetId();

      if (!userId || !testSetId) {
        console.error("사용자 ID 또는 테스트 셋 ID를 찾을 수 없습니다.");
        return;
      }

      const docId = `${userId}_${Date.now()}_${uuidv4()}`;
      const responseRef = doc(db, "userCognitiveResponse", docId);

      await setDoc(responseRef, {
        userId,
        testSetId,
        testType: "test2",
        timestamp: Timestamp.now(),
        results: {
          totalScore: score,
        },
      });

      console.log("RandomCube 결과 저장됨 (Firebase ID):", docId);
    } catch (error) {
      console.error("RandomCube 결과 저장 오류:", error);
    }
  };

  const handleFinish = async () => {
    try {
      await saveResultsToFirebase();
      navigate(`${prefix}/cognitive/check/new`);
    } catch (error) {
      console.error('Error in handleFinish:', error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack/>
        </button>
        <span className={styles.headerText}>{t('check_before_3')}</span>
      </div>
        {gameState === 'start' && (
          <React.Fragment>
          <div className={styles.startScreen}>
            <p className={styles.digitSpanInstructions1}>{t('random_cube_instructions_1')}</p>
            <div className={styles.previewCubeContainer}>
              {renderCube(
                ['1', '3', '8', '0', '+', '#', 'ㅁ'], 
                { '--cube-size': '70px' } as React.CSSProperties, 
                { transform: 'rotateX(-20deg) rotateY(45deg)' },
                styles.mainPreviewCube,
                undefined
              )}
            </div>
            <p className={styles.digitSpanInstructions2}>{t('random_cube_instructions_2')}</p>
            
            <div className={styles.previewSmallCubesContainer}>
              {Array(9).fill(null).map((_, index) => (
                <div key={index} className={`${styles.previewSmallCubeWrapper} ${styles.startOptionCubeWrapper}`}>
                  {renderCube(
                    ['1', '3', '8', '0', '+', '#', 'ㅁ'],
                    { '--cube-size': '35px' } as React.CSSProperties,
                    { transform: ROTATIONS[index % ROTATIONS.length] },
                    undefined,
                    undefined
                  )}
                </div>
              ))}
            </div>
          </div>
          <button onClick={startGame} className={styles.startButton}>
              {t("start_test_button")}
          </button>
        </React.Fragment>
        )}
        {gameState === 'playing' && (
          <div className={styles.testScreen}>
            <div className={styles.gameInfo}>
              <div className={styles.questionNumber}>{t('question')} {currentQuestion}/5</div>
              <div className={styles.timer}>{timeLeft} {t('remaining_time')}</div>
            </div>
            {renderCube(selectedChars, cubeContainerStyle, { transform: mainRotation }, undefined, undefined)}
            <div className={styles.smallCubesContainer}>
              {smallRotations.map((rotation, index) => (
                <div 
                  key={`${currentQuestion}-${index}`} 
                  className={`${styles.smallCubeWrapper} ${selectedCube === index ? styles.selected : ''}`}
                >
                  {renderCube(
                    selectedChars, 
                    playingOptionCubeStyle, 
                    { transform: rotation },
                    undefined,
                    () => handleCubeClick(index)
                  )}
                </div>
              ))}
            </div>
          </div>
        )} 
        {gameState === 'result' && (
          <React.Fragment>
          <div className={styles.resultsScreen}>
            <h2>{t('test_finished')}</h2>
            <div className={styles.resultsSummary}>
              <p>{t('total_success')}: {score}</p>
            </div>
            
          </div>
          <button onClick={handleFinish} className={styles.finishButton}>
              {t('finish_button')}
            </button>
        </React.Fragment>
        )}
    </div>
  );
};

export default RandomCube;